/**
 * Provider service for the FixMatePro frontend.
 * 
 * This service handles API calls related to provider settings, service offerings,
 * and other provider-specific functionality.
 */

import api, { API_BASE_URL } from './api';
import { getAuth } from 'firebase/auth';

// Use the consistent API base URL from api.js
const API_URL = API_BASE_URL;

// Helper function to get the current user's ID token
const getIdToken = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return await user.getIdToken();
};

// Helper function to create headers with authentication
const createAuthHeaders = async () => {
  const token = await getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Get provider settings by provider ID.
 * 
 * @param {string} providerId - The ID of the provider
 * @returns {Promise<Object>} - The provider settings
 */
export const getProviderSettings = async (providerId) => {
  try {
    // Using the configured API instance that already has the interceptor for authentication
    const response = await api.instance.get(`/provider/settings/${providerId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching provider settings:', error);
    throw error;
  }
};

/**
 * Create provider settings.
 * 
 * @param {Object} settings - The provider settings to create
 * @returns {Promise<string>} - The ID of the created settings
 */
export const createProviderSettings = async (settings) => {
  try {
    // Using the configured API instance that already has the interceptor for authentication
    const response = await api.instance.post(`/provider/settings`, settings);
    return response.data;
  } catch (error) {
    console.error('Error creating provider settings:', error);
    throw error;
  }
};

/**
 * Update provider settings.
 * 
 * @param {string} providerId - The ID of the provider
 * @param {Object} settings - The updated provider settings
 * @returns {Promise<boolean>} - True if update was successful
 */
export const updateProviderSettings = async (providerId, settings) => {
  try {
    // Using the configured API instance that already has the interceptor for authentication
    const response = await api.instance.put(`/provider/settings/${providerId}`, settings);
    return response.status === 200;
  } catch (error) {
    console.error('Error updating provider settings:', error);
    throw error;
  }
};

/**
 * Add a service offering to a provider's settings.
 * 
 * @param {string} providerId - The ID of the provider
 * @param {Object} offering - The service offering to add
 * @returns {Promise<boolean>} - True if addition was successful
 */
export const addServiceOffering = async (providerId, offering) => {
  try {
    // Using the configured API instance that already has the interceptor for authentication
    const response = await api.instance.post(`/provider/${providerId}/service-offerings`, offering);
    return response.status === 201;
  } catch (error) {
    console.error('Error adding service offering:', error);
    throw error;
  }
};

/**
 * Update a service offering.
 * 
 * @param {string} providerId - The ID of the provider
 * @param {string} offeringId - The ID of the offering to update
 * @param {Object} offering - The updated service offering
 * @returns {Promise<boolean>} - True if update was successful
 */
export const updateServiceOffering = async (providerId, offeringId, offering) => {
  try {
    // Using the configured API instance that already has the interceptor for authentication
    const response = await api.instance.put(
      `/provider/${providerId}/service-offerings/${offeringId}`, 
      offering
    );
    return response.status === 200;
  } catch (error) {
    console.error('Error updating service offering:', error);
    throw error;
  }
};

/**
 * Delete a service offering.
 * 
 * @param {string} providerId - The ID of the provider
 * @param {string} offeringId - The ID of the offering to delete
 * @returns {Promise<boolean>} - True if deletion was successful
 */
export const deleteServiceOffering = async (providerId, offeringId) => {
  try {
    // Using the configured API instance that already has the interceptor for authentication
    const response = await api.instance.delete(`/provider/${providerId}/service-offerings/${offeringId}`);
    return response.status === 204;
  } catch (error) {
    console.error('Error deleting service offering:', error);
    throw error;
  }
};

/**
 * Update AI automation settings.
 * 
 * @param {string} providerId - The ID of the provider
 * @param {Object} aiSettings - The updated AI automation settings
 * @returns {Promise<boolean>} - True if update was successful
 */
export const updateAIAutomationSettings = async (providerId, aiSettings) => {
  try {
    // Using the configured API instance that already has the interceptor for authentication
    const response = await api.instance.put(
      `/provider/ai-automation/${providerId}`,
      aiSettings
    );
    return response.status === 200;
  } catch (error) {
    console.error('Error updating AI automation settings:', error);
    throw error;
  }
};

/**
 * Update WhatsApp settings.
 * 
 * @param {string} providerId - The ID of the provider
 * @param {Object} whatsappSettings - The updated WhatsApp settings
 * @returns {Promise<boolean>} - True if update was successful
 */
export const updateWhatsAppSettings = async (providerId, whatsappSettings) => {
  try {
    // Using the configured API instance that already has the interceptor for authentication
    const response = await api.instance.put(
      `/provider/whatsapp/${providerId}`,
      whatsappSettings
    );
    return response.status === 200;
  } catch (error) {
    console.error('Error updating WhatsApp settings:', error);
    throw error;
  }
};

/**
 * Update availability.
 * 
 * @param {string} providerId - The ID of the provider
 * @param {Array} availability - The updated availability time slots
 * @returns {Promise<boolean>} - True if update was successful
 */
export const updateAvailability = async (providerId, availability) => {
  try {
    // Using the configured API instance that already has the interceptor for authentication
    const response = await api.instance.put(
      `/provider/availability/${providerId}`,
      availability
    );
    return response.status === 200;
  } catch (error) {
    console.error('Error updating availability:', error);
    throw error;
  }
};

/**
 * Update calendar connection.
 * 
 * @param {string} providerId - The ID of the provider
 * @param {Object} calendarConnection - The updated calendar connection
 * @returns {Promise<boolean>} - True if update was successful
 */
export const updateCalendarConnection = async (providerId, calendarConnection) => {
  try {
    // Using the configured API instance that already has the interceptor for authentication
    const response = await api.instance.put(
      `/provider/calendar/${providerId}`,
      calendarConnection
    );
    return response.status === 200;
  } catch (error) {
    console.error('Error updating calendar connection:', error);
    throw error;
  }
};
